import * as React from 'react';
import { Alert, PageSection, Tab, Tabs, TabTitleText, Title } from '@patternfly/react-core';
import DashBoardPermissionInterface from '@app/Interface/DashboardPermissionInterface';
import { ReactTabulator } from 'react-tabulator';
import TabulatorFormatters from '@app/utils/TabulatorFormatters';
import TabulatorCommonOptions from '@app/utils/TabulatorCommonOptions';
import moment from "moment-timezone";
import QualityComponentInterface from '@app/Interface/QualityComponentInterface';
import ItemLoader from '../ItemLoader';
import { Link } from 'react-router-dom';
import TemplateDefinitions from '@app/utils/TemplateDefinitions';
import { PageRouteContext } from '@app/Context/PageRouteContext';
import PageRouteHelper from "@app/Service/PageRouteHelper";
import { useMediaQuery } from '@react-hook/media-query';

// Tabulator Themes
import "react-tabulator/lib/styles.css"; // default theme
import "react-tabulator/css/bootstrap/tabulator_bootstrap.min.css"; // use Theme(s)
import DashboardCurrentComponentsInterface from '@app/Interface/DashboardCurrentComponentsInterface';
import QualityComponentHelper from '@app/Service/QualityComponentHelper';

import SCTabulator from '@app/utils/SCTabulator';
import options from '@app/utils/SCTabulatorCommonOptions';

interface RecentResultProps {
    dashPermissions: DashBoardPermissionInterface;
    isLoading: boolean;
    currentComponents: DashboardCurrentComponentsInterface;
}

const RecentResults: React.FC<RecentResultProps> = ({ dashPermissions, isLoading, currentComponents }) => {

    // in order to get tabulator to work with moment-timezone
    // we need to add moment to the global namespace.
    // see: https://stackoverflow.com/a/54135475/8038146
    window.moment = moment;

    const { pageRouteState } = React.useContext(PageRouteContext);

    const currentComponentsStringified = JSON.stringify(currentComponents.currentComponents);

    const currentComponentsPageRoute = PageRouteHelper.getPageRouteByReportType(pageRouteState.pageRoutes, TemplateDefinitions.REPORT_TYPE_DAILY_COMPONENTS);

    const historicalQualityPageRoute = PageRouteHelper.getPageRouteByReportType(pageRouteState.pageRoutes, TemplateDefinitions.REPORT_TYPE_HISTORICAL_QUALITY);

    const qualityComponentsPageRoute = PageRouteHelper.getPageRouteByReportType(pageRouteState.pageRoutes, TemplateDefinitions.REPORT_TYPE_CURRENT_QUALITY);

    // TODO: May need to check all process.env.PARAM, webpack may simple replace the value with nothing, which cause a build error.
    //       You need to do string (type) casting whenever process.env is used. 
    const leucTestThreshold = Number(process.env.THRESHOLD_LEUC_TEST) ?? 350;
    const leucTestCompareType = String(process.env.THRESHOLD_LEUC_TEST_COMPARE_TYPE) ?? "greater";

    const piTestThreshold = Number(process.env.THRESHOLD_PI_TEST) ?? 50;
    const piTestCompareType = String(process.env.THRESHOLD_PI_TEST_COMPARE_TYPE) ?? "greaterOrEqual";

    const waterTestThreshold = Number(process.env.THRESHOLD_WATER_TEST) ?? 524;
    const waterTestCompareType = String(process.env.THRESHOLD_WATER_TEST_COMPARE_TYPE) ?? "lesserOrEqual";

    const rawTestThreshold = Number(process.env.THRESHOLD_RAW_TEST) ?? 15;
    const rawTestCompareType = String(process.env.THRESHOLD_RAW_TEST_COMPARE_TYPE) ?? "greaterOrEqual";

    const dateTimeFormatParams = {
        inputFormat: "YYYY-MM-DD HH:ii:ss.sssssss",
        outputFormat: "MMM D",
        invalidPlaceholder: "(invalid date)",
        timezone: "America/Los_Angeles",
    };

    const formatTime = (time: number) => {
        const timeString = time.toString().padStart(4, '0'); // Ensure the time string is at least 4 characters long
        const hours = timeString.slice(0, -2);
        const minutes = timeString.slice(-2);
        return `${hours}:${minutes}`;
    };

    const isBigWindow = useMediaQuery('only screen and (min-width: 800px)');
    const maxWidth = isBigWindow ? "false" : 200;

    const tabulatorCurrentComponentColumns = [
        { title: "Date", field: "PICKUP_DATE", width: 70, frozen: true, formatter: "datetime", formatterParams: dateTimeFormatParams, resizable: false },
        { title: "Time", field: "PICKUP_TIME", width: 80, frozen: true, formatter: (cell) => formatTime(cell.getValue()), resizable: false },
        { title: "Tank", field: "TANK_NUMBER", width: 53, frozen: true, resizable: false },
        { title: "Lbs", field: "POUNDS", minWidth: 40, maxWidth: maxWidth, formatter: 'money', formatterParams: { precision: false }, resizable: false },
        { title: "Fat%", field: "BFAT_TEST", minWidth: 40, maxWidth: maxWidth, formatter: TabulatorFormatters.recentResultsFormatter, formatterParams: { decimalPlaces: 2 }, resizable: false },
        { title: "Pro%", field: "PROT_TEST", minWidth: 40, maxWidth: maxWidth, formatter: TabulatorFormatters.recentResultsFormatter, formatterParams: { decimalPlaces: 2 }, resizable: false },
        { title: "OSl%", field: "OSOL_TEST", minWidth: 40, maxWidth: maxWidth, formatter: TabulatorFormatters.recentResultsFormatter, formatterParams: { decimalPlaces: 2 }, resizable: false },
        { title: "SNF%", field: "SNF_TEST", minWidth: 40, maxWidth: maxWidth, formatter: TabulatorFormatters.recentResultsFormatter, formatterParams: { decimalPlaces: 2 }, resizable: false },
        { title: "SCC", field: "LEUC_TEST", minWidth: 40, maxWidth: maxWidth, formatter: TabulatorFormatters.recentResultsFormatter, formatterParams: { threshold: leucTestThreshold, compareType: leucTestCompareType }, resizable: false },
        { title: "M.U.N.", field: "MUN_TEST", minWidth: 40, maxWidth: maxWidth, formatter: TabulatorFormatters.recentResultsFormatter, formatterParams: { decimalPlaces: 2 }, resizable: false },
        { title: "Temp", field: "TEMPERATURE", minWidth: 40, maxWidth: maxWidth, resizable: false },
        { title: "LPC", field: "LPC_TEST", minWidth: 40, maxWidth: maxWidth, formatter: TabulatorFormatters.recentResultsFormatter, visible: true, resizable: false },
    ];

    const tabulatorCurrentQualityColumns = [
        { title: "Date", field: "PICKUP_DATE", width: 70, frozen: true, formatter: "datetime", formatterParams: dateTimeFormatParams, resizable: false },
        { title: "Time", field: "PICKUP_TIME", width: 80, frozen: true, formatter: (cell) => formatTime(cell.getValue()), resizable: false },
        { title: "Tank", field: "TANK_NUMBER", width: 53, frozen: true, resizable: false },
        { title: "Lbs", field: "POUNDS", minWidth: 40, maxWidth: maxWidth, formatter: 'money', formatterParams: { precision: false }, resizable: false },
        { title: "Raw", field: "RAW_TEST", minWidth: 40, maxWidth: maxWidth, formatter: TabulatorFormatters.recentResultsFormatter, formatterParams: { threshold: rawTestThreshold, compareType: rawTestCompareType }, resizable: false },
        { title: "PI", field: "PI_TEST", minWidth: 40, maxWidth: maxWidth, formatter: TabulatorFormatters.recentResultsFormatter, formatterParams: { threshold: piTestThreshold, compareType: piTestCompareType }, resizable: false },
        { title: "SCC", field: "LEUC_TEST", minWidth: 40, maxWidth: maxWidth, formatter: TabulatorFormatters.recentResultsFormatter, formatterParams: { threshold: leucTestThreshold, compareType: leucTestCompareType }, resizable: false },
        { title: "Water", field: "WATER_TEST", minWidth: 40, maxWidth: maxWidth, formatter: TabulatorFormatters.recentResultsFormatter, formatterParams: { threshold: waterTestThreshold, compareType: waterTestCompareType }, resizable: false },
        { title: "AB", field: "AB_TEST", minWidth: 40, maxWidth: maxWidth, formatter: TabulatorFormatters.abTestFormatter, resizable: false },
        { title: "SED", field: "SED_TEST", minWidth: 40, maxWidth: maxWidth, formatter: TabulatorFormatters.recentResultsFormatter, resizable: false },
        { title: "LPC", field: "LPC_TEST", minWidth: 40, maxWidth: maxWidth, formatter: TabulatorFormatters.recentResultsFormatter, visible: true, resizable: false },
    ];

    let defaultTabKey = 0;

    if (dashPermissions.viewCurrentComponent === false) {
        defaultTabKey = 1;
    }

    let hasLpcData = false;

    if (Array.isArray(currentComponents.currentComponents)) {
        hasLpcData = QualityComponentHelper.hasLpcData(currentComponents.currentComponents);
    }

    if (hasLpcData === false) {

        // hide LPC columns
        const currentComponentLpcIndex = tabulatorCurrentComponentColumns.findIndex(element => element.field === "LPC_TEST");

        const currentComponentLpcColumn = tabulatorCurrentComponentColumns[currentComponentLpcIndex];

        currentComponentLpcColumn.visible = false;

        const currentQualityLpcIndex = tabulatorCurrentQualityColumns.findIndex(element => element.field === "LPC_TEST");

        const currentQualityLpcColumn = tabulatorCurrentQualityColumns[currentQualityLpcIndex];

        currentQualityLpcColumn.visible = false;

    }

    const [currentComponentsRows, setCurrentComponentsRows] = React.useState<QualityComponentInterface[]>([]);

    const [qualityComponentsRows, setQualityComponentsRows] = React.useState<QualityComponentInterface[]>([]);

    const [activeTabKey, setActiveTabKey] = React.useState(defaultTabKey);

    const tabClickHandler = (event: React.MouseEvent<HTMLElement, MouseEvent>, tabIndex: string | number) => {

        if (typeof tabIndex === "number") {
            setActiveTabKey(tabIndex);
        }

    };

    const tabulatorOptions = {
        headerSort: false, //disable header sort for all columns
        paginationSize: 5,
        cellHozAlign: "right",
        headercellHozAlign: "right"
    };

    const missingDataAlert = (
        <Alert
            variant="warning"
            title="No Data"
            aria-live="polite"
            role="alert"
        >
            <p>No data was found for the producer.</p>
        </Alert>
    );

    // qualityComponents table doesn't rerender when switching users
    // maybe forcing empty data during loading will work
    React.useEffect(() => {

        if (isLoading) {

            setCurrentComponentsRows([]);

            setQualityComponentsRows([]);

        }

    }, [isLoading]);

    // check for new current data
    React.useEffect(() => {

        // check currentComponents data
        if (currentComponents.canUserViewComponents && Array.isArray(currentComponents.currentComponents)) {
            setCurrentComponentsRows([...currentComponents.currentComponents]);
        }


        // check currentComponents data
        if (currentComponents.canUserViewQualty && Array.isArray(currentComponents.currentQuality)) {
            setQualityComponentsRows([...currentComponents.currentQuality]);
        }

    }, [currentComponents.canUserViewComponents, currentComponents.canUserViewQualty, currentComponents.currentComponents, currentComponents.currentQuality, currentComponentsStringified]);

    return (
        <PageSection>
            <Title headingLevel={'h2'} className="pf-u-mb-md">Recent Results</Title>
            <Tabs mountOnEnter activeKey={activeTabKey} onSelect={tabClickHandler} isBox>
                <Tab eventKey={0} title={<TabTitleText>Components</TabTitleText>} isHidden={!dashPermissions.viewCurrentComponent}>
                    <div id="current-components" className="pf-u-background-color-100 pf-u-p-md">
                        <div className="pf-u-my-md">
                            Current Components
                        </div>
                        {isLoading
                            ? <ItemLoader />
                            : currentComponentsRows.length === 0
                                ? missingDataAlert
                                : <SCTabulator
                                    data={currentComponentsRows}
                                    columns={tabulatorCurrentComponentColumns}
                                    layout={isBigWindow ? "fitColumns" : "fitData"}
                                    options={{
                                        ...options('nda-recent-results-current-components-sc-no-move', true, false), ...tabulatorOptions
                                    }}
                                    key={0}
                                />
                        }
                        <div className="pf-u-mt-md">
                            {currentComponentsPageRoute.id > 0 && <Link to={{ pathname: currentComponentsPageRoute.path }}>Current link</Link>}
                            |
                            {historicalQualityPageRoute.id > 0 && <Link to={{ pathname: historicalQualityPageRoute.path }}>Historic link</Link>}
                        </div>
                    </div>
                </Tab>
                <Tab eventKey={1} title={<TabTitleText>Quality</TabTitleText>} isHidden={!dashPermissions.viewQualityComponents}>
                    <div id="quality-components" className="pf-u-background-color-100 pf-u-p-md">
                        <div className="pf-u-my-md">
                            Current Quality
                        </div>
                        {isLoading
                            ? <ItemLoader />
                            : qualityComponentsRows.length === 0
                                ? missingDataAlert
                                : <SCTabulator
                                    data={qualityComponentsRows}
                                    columns={tabulatorCurrentQualityColumns}
                                    layout={isBigWindow ? "fitColumns" : "fitData"}
                                    options={{
                                        ...options('nda-recent-results-quality-components-sc-no-move', true, false), ...tabulatorOptions
                                    }}
                                    key={1}
                                /> 
                        }
                        <div className="pf-u-mt-md">
                            {qualityComponentsPageRoute.id > 0 && <Link to={{ pathname: qualityComponentsPageRoute.path }}>Current link</Link>}
                            |
                            {historicalQualityPageRoute.id > 0 && <Link to={{ pathname: historicalQualityPageRoute.path }}>Historic link</Link>}
                        </div>
                    </div>
                </Tab>
            </Tabs>
        </PageSection>
    );

}

export default RecentResults;